import { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { addBiomarker, validateBiomarker } from "@/api/catalogs";
import { getSchema } from "@/api/schema";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/**
 * Phase 4 (ADR-0040 authoring) — draft a new biomarker.
 *
 * Renders a form for the core `BiomarkerDef` fields, validates client-side
 * (grounded by the live `GET /api/v1/schema/biomarker-def` required list), and
 * produces the exact JSON an author would submit. MR submission is intentionally
 * NOT wired: the onboarding-api write endpoints are stubbed pending ADR-0040 Q1
 * (GitLab token type). The "Copy JSON" action makes the draft usable today (paste
 * into a manual MR); the disabled "Submit as MR" button marks where the write path
 * lands. When the endpoint ships, wire `onSubmit` to it — the form + payload are
 * already correct.
 */

interface FormValues {
  name: string;
  display: string;
  abbreviation: string;
  panel_name: string;
  sample_type: string;
  coverage_type: string;
  allowed_categories: string[];
  unit_code: string;
  loinc: string;
}

/** Per-category [min, max) bounds as raw input strings (empty = not authored). */
export interface RangeInput {
  min: string;
  max: string;
}
export type RangeMap = Record<string, RangeInput>;

/** Raw demographic-filter inputs — "" / blank means "no constraint on that field"
 * (ADR-0035; a bucket with no constraints is the fallback that matches everyone). */
export interface DemographicFilterInput {
  sex: "" | "male" | "female";
  ageMin: string;
  ageMax: string;
  pregnant: "" | "yes" | "no";
}

export const EMPTY_FILTER: DemographicFilterInput = {
  sex: "",
  ageMin: "",
  ageMax: "",
  pregnant: "",
};

/** One authored range bucket: an optional demographic filter + per-category bounds. */
export interface BucketDraft {
  id: string;
  filter: DemographicFilterInput;
  ranges: RangeMap;
}

/** The `demographic_filter` payload (only the set fields), or null when a bucket
 * constrains nothing (the fallback). Mirrors BiomarkerDef.RangeBucket. */
export function buildDemographicFilter(f: DemographicFilterInput): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (f.sex !== "") out.sex = f.sex;
  if (f.ageMin.trim() !== "") out.age_min = Number(f.ageMin);
  if (f.ageMax.trim() !== "") out.age_max = Number(f.ageMax);
  if (f.pregnant !== "") out.pregnant = f.pregnant === "yes";
  return Object.keys(out).length > 0 ? out : null;
}

/** Range-editor errors across all buckets (partial/inverted ranges; inverted age). */
export function computeRangeErrors(categories: string[], buckets: BucketDraft[]): string[] {
  const errs: string[] = [];
  buckets.forEach((b, i) => {
    const label = buckets.length > 1 ? `Bucket ${i + 1}` : "Range";
    for (const cat of categories) {
      const r = b.ranges[cat] ?? { min: "", max: "" };
      const hasMin = r.min.trim() !== "";
      const hasMax = r.max.trim() !== "";
      if (hasMin !== hasMax)
        errs.push(`${label} · ${cat}: set both min and max, or leave both blank.`);
      else if (hasMin && hasMax && Number(r.min) > Number(r.max))
        errs.push(`${label} · ${cat}: min must be ≤ max.`);
    }
    const { ageMin, ageMax } = b.filter;
    if (ageMin.trim() !== "" && ageMax.trim() !== "" && Number(ageMin) > Number(ageMax))
      errs.push(`${label}: age min must be ≤ age max.`);
  });
  return errs;
}

/** Assemble the BiomarkerDef payload from the form + range buckets. Each bucket
 * with ≥1 fully-filled category becomes a range_buckets entry (with its
 * demographic_filter when constrained); empty buckets are dropped. */
export function buildBiomarkerDraft(
  v: FormValues,
  buckets: BucketDraft[] = [],
): Record<string, unknown> {
  const d: Record<string, unknown> = {
    name: v.name.trim(),
    display: v.display.trim(),
  };
  if (v.abbreviation.trim() !== "") d.abbreviation = v.abbreviation.trim();
  if (v.panel_name.trim() !== "") d.panel_name = v.panel_name.trim();
  if (v.sample_type.trim() !== "") d.sample_type = v.sample_type.trim();
  if (v.coverage_type.trim() !== "") d.coverage_type = v.coverage_type.trim();
  d.allowed_categories = v.allowed_categories;
  d.units = [{ code: v.unit_code.trim(), is_canonical: true }];
  d.codes = { loinc: v.loinc.trim() || null, snomed: null, secondary_loinc: [] };

  const unit = v.unit_code.trim();
  const rangeBuckets: Record<string, unknown>[] = [];
  for (const b of buckets) {
    const ranges: Record<string, [number, number]> = {};
    for (const cat of v.allowed_categories) {
      const r = b.ranges[cat];
      if (r !== undefined && r.min.trim() !== "" && r.max.trim() !== "") {
        ranges[cat] = [Number(r.min), Number(r.max)];
      }
    }
    if (Object.keys(ranges).length === 0) continue;
    const bucket: Record<string, unknown> = { unit, ranges };
    const filter = buildDemographicFilter(b.filter);
    if (filter !== null) bucket.demographic_filter = filter;
    rangeBuckets.push(bucket);
  }
  if (rangeBuckets.length > 0) d.range_buckets = rangeBuckets;
  return d;
}

export function NewBiomarkerPage(): JSX.Element {
  const { org = "", branch } = useParams<{ org: string; branch?: string }>();
  const clipboard = useClipboard({ timeout: 1500 });

  const backPath = branch
    ? `/catalogs/${encodeURIComponent(org)}/branch/${encodeURIComponent(branch)}`
    : `/catalogs/${encodeURIComponent(org)}`;

  // Schema fetch is a non-blocking authoring hint (shows required fields).
  const { data: schema } = useQuery({
    queryKey: ["schema", "biomarker-def"],
    queryFn: () => getSchema("biomarker-def"),
  });

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      display: "",
      abbreviation: "",
      panel_name: "",
      sample_type: "",
      coverage_type: "",
      allowed_categories: [],
      unit_code: "",
      loinc: "",
    },
    validate: {
      name: (v) =>
        v.trim() === ""
          ? t("new_biomarker.err_required")
          : v.length > 20
            ? t("new_biomarker.err_name_len")
            : null,
      display: (v) =>
        v.trim() === ""
          ? t("new_biomarker.err_required")
          : v.length > 75
            ? t("new_biomarker.err_display_len")
            : null,
      abbreviation: (v) => (v.length > 20 ? t("new_biomarker.err_abbrev_len") : null),
      allowed_categories: (v) => (v.length === 0 ? t("new_biomarker.err_categories") : null),
      unit_code: (v) => (v.trim() === "" ? t("new_biomarker.err_required") : null),
    },
    validateInputOnBlur: true,
  });

  const [buckets, setBuckets] = useState<BucketDraft[]>([
    { id: "b0", filter: { ...EMPTY_FILTER }, ranges: {} },
  ]);
  const nextId = useRef(1);

  const setBucketRange = (
    bi: number,
    cat: string,
    field: keyof RangeInput,
    value: string,
  ): void => {
    setBuckets((bs) =>
      bs.map((b, i) =>
        i !== bi
          ? b
          : {
              ...b,
              ranges: {
                ...b.ranges,
                [cat]: { min: "", max: "", ...b.ranges[cat], [field]: value },
              },
            },
      ),
    );
  };
  const setBucketFilter = (
    bi: number,
    field: keyof DemographicFilterInput,
    value: string,
  ): void => {
    setBuckets((bs) =>
      bs.map((b, i) => (i !== bi ? b : { ...b, filter: { ...b.filter, [field]: value } })),
    );
  };
  const addBucket = (): void => {
    setBuckets((bs) => [
      ...bs,
      { id: `b${nextId.current++}`, filter: { ...EMPTY_FILTER }, ranges: {} },
    ]);
  };
  const removeBucket = (bi: number): void => {
    setBuckets((bs) => bs.filter((_, i) => i !== bi));
  };

  const rangeErrors = computeRangeErrors(form.values.allowed_categories, buckets);
  const draft = buildBiomarkerDraft(form.values, buckets);
  const draftJson = JSON.stringify(draft, null, 2);

  const handleCopy = (): void => {
    if (form.validate().hasErrors || rangeErrors.length > 0) {
      notifications.show({ color: "red", message: t("new_biomarker.copy_blocked") });
      return;
    }
    clipboard.copy(draftJson);
    notifications.show({ color: "green", message: t("new_biomarker.copied_toast") });
  };

  const submitMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => addBiomarker(org, payload),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        title: t("new_biomarker.mr_opened"),
        message: res.mr_url,
        autoClose: false,
      });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      // 503 = the write path isn't provisioned server-side yet (stub client).
      notifications.show({
        color: "red",
        message: msg.includes("503") ? t("new_biomarker.submit_disabled") : msg,
      });
    },
  });

  const handleSubmit = (): void => {
    if (form.validate().hasErrors || rangeErrors.length > 0) {
      notifications.show({ color: "red", message: t("new_biomarker.copy_blocked") });
      return;
    }
    submitMutation.mutate(draft);
  };

  const validateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => validateBiomarker(org, payload),
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const handleValidate = (): void => {
    if (form.validate().hasErrors || rangeErrors.length > 0) {
      notifications.show({ color: "red", message: t("new_biomarker.copy_blocked") });
      return;
    }
    validateMutation.mutate(draft);
  };

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("catalogs.title"), to: "/catalogs" },
        { label: branch ? `${org} / ${branch}` : org, to: backPath },
        { label: t("new_biomarker.title") },
      ]}
    >
      <Stack gap="md" maw={720}>
        <Title order={1}>{t("new_biomarker.title")}</Title>

        {schema?.schema.required !== undefined && (
          <Group gap={4}>
            <Text size="xs" c="dimmed">
              {t("new_biomarker.required_hint")}
            </Text>
            {schema.schema.required.map((r) => (
              <Badge key={r} size="xs" variant="outline">
                {r}
              </Badge>
            ))}
          </Group>
        )}

        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <TextInput
              label={t("catalog_detail.col_symbol")}
              description={t("new_biomarker.symbol_desc")}
              withAsterisk
              {...form.getInputProps("name")}
            />
            <TextInput
              label={t("catalog_detail.col_display")}
              withAsterisk
              {...form.getInputProps("display")}
            />
            <TextInput
              label={t("catalog_detail.col_abbrev")}
              {...form.getInputProps("abbreviation")}
            />
            <TextInput
              label={t("catalog_detail.col_panel")}
              {...form.getInputProps("panel_name")}
            />
            <Group grow>
              <TextInput
                label={t("biomarker.sample_type")}
                {...form.getInputProps("sample_type")}
              />
              <TextInput
                label={t("biomarker.coverage_type")}
                {...form.getInputProps("coverage_type")}
              />
            </Group>
            <TagsInput
              label={t("biomarker.categories")}
              description={t("new_biomarker.categories_desc")}
              withAsterisk
              {...form.getInputProps("allowed_categories")}
            />
            <TextInput
              label={t("new_biomarker.canonical_unit")}
              description={t("new_biomarker.unit_desc")}
              withAsterisk
              {...form.getInputProps("unit_code")}
            />
            <TextInput label="LOINC" {...form.getInputProps("loinc")} />
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={3}>{t("new_biomarker.ranges_title")}</Title>
              {form.values.allowed_categories.length > 0 && (
                <Button size="xs" variant="light" onClick={addBucket}>
                  {t("new_biomarker.add_bucket")}
                </Button>
              )}
            </Group>
            {form.values.allowed_categories.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t("new_biomarker.ranges_need_categories")}
              </Text>
            ) : (
              <>
                <Text size="xs" c="dimmed">
                  {t("new_biomarker.ranges_desc")}
                </Text>
                {buckets.map((b, bi) => (
                  <Paper key={b.id} withBorder p="sm" radius="sm">
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" fw={600}>
                          {buildDemographicFilter(b.filter) === null
                            ? t("new_biomarker.bucket_default")
                            : `${t("new_biomarker.bucket_n")} ${bi + 1}`}
                        </Text>
                        {buckets.length > 1 && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            onClick={() => removeBucket(bi)}
                          >
                            {t("new_biomarker.remove")}
                          </Button>
                        )}
                      </Group>
                      <Group grow align="flex-end">
                        <Select
                          label={t("new_biomarker.filter_sex")}
                          data={[
                            { value: "", label: t("new_biomarker.any") },
                            { value: "male", label: "male" },
                            { value: "female", label: "female" },
                          ]}
                          value={b.filter.sex}
                          onChange={(v) => setBucketFilter(bi, "sex", v ?? "")}
                          allowDeselect={false}
                        />
                        <NumberInput
                          label={t("new_biomarker.filter_age_min")}
                          min={0}
                          max={130}
                          value={b.filter.ageMin === "" ? "" : Number(b.filter.ageMin)}
                          onChange={(v) => setBucketFilter(bi, "ageMin", v === "" ? "" : String(v))}
                        />
                        <NumberInput
                          label={t("new_biomarker.filter_age_max")}
                          min={0}
                          max={130}
                          value={b.filter.ageMax === "" ? "" : Number(b.filter.ageMax)}
                          onChange={(v) => setBucketFilter(bi, "ageMax", v === "" ? "" : String(v))}
                        />
                        <Select
                          label={t("new_biomarker.filter_pregnant")}
                          data={[
                            { value: "", label: t("new_biomarker.any") },
                            { value: "yes", label: "yes" },
                            { value: "no", label: "no" },
                          ]}
                          value={b.filter.pregnant}
                          onChange={(v) => setBucketFilter(bi, "pregnant", v ?? "")}
                          allowDeselect={false}
                        />
                      </Group>
                      {form.values.allowed_categories.map((cat) => (
                        <Group key={cat} gap="sm" grow align="flex-end">
                          <Text size="sm" fw={500}>
                            {cat}
                          </Text>
                          <NumberInput
                            label={t("new_biomarker.range_min")}
                            value={b.ranges[cat]?.min ?? ""}
                            onChange={(v) =>
                              setBucketRange(bi, cat, "min", v === "" ? "" : String(v))
                            }
                          />
                          <NumberInput
                            label={t("new_biomarker.range_max")}
                            value={b.ranges[cat]?.max ?? ""}
                            onChange={(v) =>
                              setBucketRange(bi, cat, "max", v === "" ? "" : String(v))
                            }
                          />
                        </Group>
                      ))}
                    </Stack>
                  </Paper>
                ))}
                {rangeErrors.map((e) => (
                  <Text key={e} size="xs" c="red">
                    {e}
                  </Text>
                ))}
              </>
            )}
          </Stack>
        </Paper>

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            {t("new_biomarker.preview")}
          </Text>
          <Code block>{draftJson}</Code>
        </Stack>

        <Group>
          <Button variant="light" onClick={handleCopy}>
            {clipboard.copied ? t("new_biomarker.copied") : t("new_biomarker.copy")}
          </Button>
          <Button variant="default" onClick={handleValidate} loading={validateMutation.isPending}>
            {t("new_biomarker.validate")}
          </Button>
          <Button onClick={handleSubmit} loading={submitMutation.isPending}>
            {t("new_biomarker.submit")}
          </Button>
        </Group>

        {validateMutation.data !== undefined && (
          <Alert
            color={
              validateMutation.data.errors.length > 0
                ? "red"
                : validateMutation.data.warnings.length > 0
                  ? "yellow"
                  : "green"
            }
            title={
              validateMutation.data.is_valid
                ? t("new_biomarker.validate_ok_title")
                : t("new_biomarker.validate_fail_title")
            }
          >
            {validateMutation.data.errors.length === 0 &&
            validateMutation.data.warnings.length === 0 ? (
              t("new_biomarker.validate_ok")
            ) : (
              <Stack gap={2}>
                {validateMutation.data.errors.map((e) => (
                  <Text key={`e-${e.message}`} size="sm">
                    ✗ {e.message}
                  </Text>
                ))}
                {validateMutation.data.warnings.map((w) => (
                  <Text key={`w-${w.message}`} size="sm">
                    ⚠ {w.message}
                  </Text>
                ))}
              </Stack>
            )}
          </Alert>
        )}

        <Alert color="blue" variant="light" title={t("new_biomarker.gate_title")}>
          {t("new_biomarker.gate_body")}
        </Alert>
      </Stack>
    </AppLayout>
  );
}
